import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCourseById } from "../../services/courseService";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft, 
  faClock,
  faCertificate,
  faUserGraduate,
  faPlayCircle,
  faCheckCircle,
  faStar,
  faBookOpen,
  faUserTie,
  faDollarSign,
  faLayerGroup
} from "@fortawesome/free-solid-svg-icons";

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const data = await getCourseById(courseId);
        setCourse(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Course Not Found</h1>
          <button
            onClick={() => navigate("/courses")}
            className="px-6 py-3 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-all"
          >
            Browse Courses
          </button>
        </div>
      </div>
    );
  }

  // Determine level colors based on course difficulty
  const getLevelConfig = (niveau) => {
    switch (niveau?.toLowerCase()) {
      case "beginner":
        return {
          bg: "bg-gradient-to-br from-emerald-50 to-teal-100",
          color: "from-emerald-400 to-teal-500",
          badge: "bg-emerald-100 text-emerald-800",
        };
      case "intermediate":
        return {
          bg: "bg-gradient-to-br from-blue-50 to-indigo-100",
          color: "from-blue-400 to-indigo-500",
          badge: "bg-blue-100 text-blue-800",
        };
      case "advanced":
        return {
          bg: "bg-gradient-to-br from-purple-50 to-pink-100",
          color: "from-purple-400 to-pink-500",
          badge: "bg-purple-100 text-purple-800",
        };
      default:
        return {
          bg: "bg-gradient-to-br from-gray-50 to-gray-100",
          color: "from-gray-400 to-gray-600",
          badge: "bg-gray-100 text-gray-800",
        };
    }
  };

  const level = getLevelConfig(course.niveau);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white"
    >
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className={`relative ${level.bg} py-8 px-4 sm:px-6 lg:px-8`}>
          <div className="max-w-7xl mx-auto">
            {/* Back button */}
            <motion.button
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-8 group"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-sm group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Courses</span>
            </motion.button>

     
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* What you'll learn */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-600 text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Ce que vous apprendrez</h2>
              </div>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="leading-relaxed">{course.learn}</p>
              </div>
            </motion.section>

            {/* Programme */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faBookOpen} className="text-blue-600 text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Programme de formation</h2>
              </div>
              <div className="space-y-4">
                  
                    <div className="flex-1">
                         {course.programme?.map((item, index) => (
  <div
    key={index}
    className="flex gap-4 p-4 rounded-xl hover:bg-gray-50 transition-all group"
  >
    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
      <FontAwesomeIcon icon={faPlayCircle} className="text-blue-600" />
    </div>

    <div className="flex-1">
      <h3 className="font-semibold text-gray-900">
        {item.chapter}
      </h3>
      <p className="text-gray-600 text-sm mt-1">
        {item.description}
      </p>
    </div>
  </div>
))}

                    </div>
              </div>
            </motion.section>

            {/* Instructor */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faUserTie} className="text-purple-600 text-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Votre formateur</h2>
              </div>
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 p-1">
                  <div className="w-full h-full bg-white rounded-2xl flex items-center justify-center">
                    <div className="text-4xl bg-gradient-to-br from-purple-400 to-pink-500 bg-clip-text text-transparent">
                      {course.prof_name?.[0]}
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{course.prof_name}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{course.prof_email}</p>
                  <p className="text-gray-600 mb-4 leading-relaxed">{course.prof_bio}</p>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Réalisations & Certifications</h4>
                    <p className="text-gray-600">{course.prof_achievements}</p>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Course Details Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 top-8"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-6">Détails</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-semibold text-gray-900">{course.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Niveau</p>
                    <p className="font-semibold text-gray-900">{course.niveau}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <FontAwesomeIcon icon={faClock} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Duration</p>
                    <p className="font-semibold text-gray-900">{course.duree}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                    <FontAwesomeIcon icon={faCertificate} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Certificate</p>
                    <p className={`font-semibold ${course.certificat ? 'text-green-600' : 'text-gray-900'}`}>
                      {course.certificat ? 'Included' : 'Not Included'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Prerequisites */}
            {course.prerequis && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Prérequis</h3>
                <div className="space-y-3">
                  {course.prerequis.split(',').map((req, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-gray-600">{req.trim()}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CourseDetailsPage;